<script setup lang="ts">
  /**
   * Modals — 03-components/25-modals · OSCloud DS 1.1.154
   */
  import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'

  import BfButton from './BfButton.vue'

  type Variant = 'confirmation' | 'record' | 'plain'
  type Measure = 'compact' | 'base'

  export interface BfModalMetaItem {
    label: string
    value: string
  }

  const props = withDefaults(
    defineProps<{
      open?: boolean
      title?: string
      /**
       * `confirmation` — the rounded dialog in the product's voice. `record` — a stored document shown verbatim
       * (720px, full-bleed strips, scrolling body); never combined with `measure` or `scrollable`. `plain` — the bare
       * shell.
       */
      variant?: Variant
      /** `compact` (560px) for one message or a short form — the default; `base` (1028px) for a table or columns. */
      measure?: Measure
      /** For a dialog whose content can outgrow a laptop viewport — any form-bearing dialog. */
      scrollable?: boolean
      /** A genuinely needed third width, set as `--modal-measure`. Never a max-width on the panel. */
      modalMeasure?: string
      /** The record's envelope — zero to six read-only pairs. `record` only. */
      meta?: BfModalMetaItem[]
      /** Dresses the confirm button as the destructive action. */
      destructive?: boolean
      confirmLabel?: string
      cancelLabel?: string
      /** The cancel button's dress — `outline` by default; `ghost` when the dialog is read-only and Cancel only closes it. */
      cancelVariant?: 'outline' | 'ghost'
      /** The single footer action of a `record` dialog. */
      dismissLabel?: string
      closeLabel?: string
    }>(),
    {
      open: false,
      title: '',
      variant: 'confirmation',
      measure: 'compact',
      scrollable: false,
      modalMeasure: undefined,
      meta: () => [],
      destructive: false,
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      cancelVariant: 'outline',
      dismissLabel: 'Close',
      closeLabel: 'Close dialog',
    },
  )

  const emit = defineEmits<{
    'update:open': [open: boolean]
    confirm: []
    cancel: []
  }>()

  const titleId = useId()

  const isRecord = computed(() => props.variant === 'record')

  const panelClasses = computed(() => ({
    'modal--confirmation': props.variant === 'confirmation',
    'modal--record': isRecord.value,
    'modal--compact': !isRecord.value && props.measure === 'compact',
    'modal--scrollable': !isRecord.value && props.scrollable,
  }))

  const panelStyle = computed(() => (props.modalMeasure ? { '--modal-measure': props.modalMeasure } : undefined))

  const panel = ref<HTMLElement | null>(null)
  const closeButton = ref<HTMLElement | null>(null)
  let lastTrigger: HTMLElement | null = null

  const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

  const close = (): void => {
    emit('update:open', false)
  }

  const onCancel = (): void => {
    emit('cancel')
    close()
  }

  const onConfirm = (): void => {
    emit('confirm')
    close()
  }

  // Clicking outside the panel closes, the way the card's overlay does.
  const onOverlayClick = (event: MouseEvent): void => {
    if (panel.value && !panel.value.contains(event.target as Node)) onCancel()
  }

  const onKeydown = (event: KeyboardEvent): void => {
    if (!props.open) return

    if (event.key === 'Escape') {
      onCancel()
      return
    }

    if (event.key !== 'Tab' || !panel.value) return

    const focusable = Array.from(panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      element => !element.hasAttribute('disabled') && element.offsetParent !== null,
    )
    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const release = (): void => {
    document.removeEventListener('keydown', onKeydown)
    document.body.style.overflow = ''
  }

  watch(
    () => props.open,
    async isOpen => {
      if (isOpen) {
        lastTrigger = document.activeElement as HTMLElement | null
        document.addEventListener('keydown', onKeydown)
        document.body.style.overflow = 'hidden'
        await nextTick()
        closeButton.value?.focus()
        return
      }

      release()
      lastTrigger?.focus()
    },
    // Store-driven wrappers (IndexModal) mount the dialog already open.
    { immediate: true },
  )

  onBeforeUnmount(() => {
    release()
    if (props.open) lastTrigger?.focus()
  })
</script>

<template>
  <div
    v-if="open"
    class="modal-overlay"
    :class="{ 'modal-overlay--confirmation': variant === 'confirmation' }"
    @click="onOverlayClick"
  >
    <div class="modal-overlay__frame">
      <div
        ref="panel"
        class="modal"
        :class="panelClasses"
        :style="panelStyle"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : undefined"
      >
        <div class="modal__content">
          <div class="modal__header">
            <h2 v-if="title" :id="titleId" class="modal__title">{{ title }}</h2>
            <button
              ref="closeButton"
              type="button"
              class="modal__close-button"
              :aria-label="closeLabel"
              @click="onCancel"
            >
              &times;
            </button>
          </div>

          <dl v-if="isRecord && meta.length" class="modal__meta">
            <template v-for="item in meta" :key="item.label">
              <dt class="modal__meta-label">{{ item.label }}</dt>
              <dd class="modal__meta-value">{{ item.value }}</dd>
            </template>
          </dl>

          <div class="modal__body"><slot /></div>

          <div class="modal__footer">
            <slot name="footer">
              <BfButton v-if="isRecord" variant="primary" size="small" @click="onCancel">{{ dismissLabel }}</BfButton>
              <template v-else>
                <BfButton :variant="cancelVariant" size="small" @click="onCancel">{{ cancelLabel }}</BfButton>
                <BfButton
                  :variant="destructive ? 'danger' : 'primary'"
                  size="small"
                  :sweep="!destructive"
                  @click="onConfirm"
                >
                  {{ confirmLabel }}
                </BfButton>
              </template>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .modal-overlay {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 1000;
    background: #00000040;
    overflow: auto;

    &--confirmation {
      background: rgba(0, 0, 0, 0.4);
    }

    &__frame {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 100%;
      margin: 0 12px;
    }
  }

  .modal {
    --modal-measure: 1028px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    box-shadow: 0 2px 16px rgb(0 0 0 / 10%);
    min-height: 170px;
    min-width: 280px;
    width: 100%;
    max-width: var(--modal-measure);
    padding: 20px 26px;
    margin: 0 auto;
    overflow: visible;
    background-color: white;
    background-size: cover;

    &--confirmation {
      min-height: auto;
      border-radius: var(--bf-r-xl);
      box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
    }

    &--compact {
      --modal-measure: 560px; /* = --confirmation-panel-measure (13b), repeated so the two blocks move independently */
    }

    &--scrollable {
      align-items: stretch;
      max-height: calc(100vh - 64px); /* two --space-4 gaps, deliberately a literal */
    }

    &--scrollable &__content {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    &--scrollable &__body {
      overflow-y: auto;
      min-height: 0;
    }

    &__content {
      width: 100%;
      padding: 12px 6px;
    }

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 14px;
    }

    &__title {
      margin: 0;
      font-family: var(--bf-font-display);
      font-size: 18px;
      font-weight: var(--bf-fw-semibold);
      line-height: 1.25;
      letter-spacing: -0.01em;
      color: var(--bf-deep-blue);
    }

    &__close-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      padding: 5px;
      border: 1px solid transparent; /* load-bearing: the hover edge must not resize the 32 × 32 box */
      border-radius: var(--bf-r-sm);
      background: none;
      box-shadow: none; /* overrides the legacy global button:focus halo, which the DS card never has */
      cursor: pointer;
      font-family: var(--bf-font-body);
      font-size: 20px;
      line-height: 1;
      color: var(--bf-dusky-1);
      transition:
        color var(--bf-dur-fast) var(--bf-ease),
        border-color var(--bf-dur-fast) var(--bf-ease);

      &:hover {
        color: var(--bf-orange-dark);
        border-color: var(--bf-orange-dark);
        background: none;
      }

      &:focus-visible {
        outline: 2px solid var(--bf-orange-dark);
        outline-offset: 1px;
      }
    }

    &__body {
      margin: 0;
      font-family: var(--bf-font-body);
      font-size: 14px;
      line-height: var(--bf-lh-relaxed);
      color: var(--bf-dusky-1);
      white-space: pre-line;

      &:has(> *) {
        white-space: normal;
      }
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 24px;
    }

    &--record {
      --modal-measure: 720px; /* = --page-measure, one column of prose */
      align-items: stretch;
      max-height: calc(100vh - 64px);
      min-height: auto;
      padding: 0;
      border-radius: var(--bf-r-lg);
      box-shadow: var(--bf-shadow-3);
      overflow: hidden;
    }

    &--record &__content {
      display: flex;
      flex-direction: column;
      min-height: 0;
      padding: 0;
    }

    &--record &__header {
      position: relative;
      margin: 0;
      padding: 22px 24px 20px;
      border-bottom: 1px solid var(--bf-line);
    }

    &--record &__header::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -1px;
      width: 64px;
      height: 2px;
      background: var(--bf-orange-dark);
    }

    &--record &__title {
      font-size: var(--bf-fs-lg);
      font-weight: var(--bf-fw-bold);
      line-height: var(--bf-lh-snug);
      letter-spacing: var(--bf-tr-tight);
      color: var(--bf-fg-1);
    }

    &__meta {
      display: grid;
      grid-template-columns: auto 1fr;
      row-gap: 8px;
      column-gap: 14px;
      margin: 0;
      padding: 16px 24px;
      background: var(--bf-blue-light);
      border-bottom: 1px solid var(--bf-line);
    }

    &__meta-label {
      align-self: center;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-2xs);
      font-weight: var(--bf-fw-bold);
      color: var(--bf-fg-2);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-caps-lg);
    }

    &__meta-value {
      margin: 0;
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-xs_5);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      font-variant-numeric: tabular-nums;
      overflow-wrap: anywhere;
    }

    &--record &__body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      margin: 0;
      padding: 24px;
      background: var(--bf-white);
      font-size: var(--bf-fs-sm);
      line-height: var(--bf-lh-relaxed);
      color: var(--bf-fg-2);
      white-space: pre-wrap;
    }

    &--record &__body:has(> *) {
      white-space: normal;
    }

    &--record &__footer {
      margin-top: 0;
      padding: 14px 24px;
      border-top: 1px solid var(--bf-line);
      background: var(--bf-blue-light);
    }
  }

  @media (max-width: 480px) {
    .modal__footer {
      flex-direction: column-reverse;
      align-items: stretch;
    }
  }

  @media (max-width: 600px) {
    .modal--record .modal__header,
    .modal--record .modal__meta,
    .modal--record .modal__body,
    .modal--record .modal__footer {
      padding-left: 18px;
      padding-right: 18px;
    }

    .modal--record .modal__title {
      font-size: var(--bf-fs-md);
    }
  }
</style>
